import { Box } from '@material-ui/core';
import { SBase } from '@setvi/shared/components';
import { ExpiredWarning } from 'pages/quotes-v2/components';
import { useQuoteConfigStore } from 'pages/auto-rfq/store/rfqConfig';
import moment from 'moment';
import { useQuoteDetailsForm } from './hooks/use-quote-details-form';
import { useQuoteDetailsMutations } from './hooks/use-quote-details-mutations';
import { useQuoteDetailsSteps } from './hooks/use-quote-details-steps';
import { useQuoteDetailsSave } from './hooks/use-quote-details-save';
import { StepperSidebar } from './components/stepper-sidebar';
import { ValidationWarning } from './components/validation-warning';
import { ModalFooter } from './components/modal-footer';
import { StepContentRenderer } from './components/step-content';
import { Content, ContentContainer } from './style';
import { QUOTE_DETAILS_DIALOG, QuoteDetailsSteps } from './types';
import type { QuoteDetailsModalProps } from './types';

export { QUOTE_DETAILS_DIALOG, QuoteDetailsSteps };

export const QuoteDetailsModal = ({
  updateQuoteInfo,
  onClose,
  quote,
  step = QuoteDetailsSteps.PREPARED_FOR
}: QuoteDetailsModalProps) => {
  const { validUntil, enableValidityDate } = useQuoteConfigStore();

  const form = useQuoteDetailsForm(quote);
  const mutations = useQuoteDetailsMutations();

  const stepsState = useQuoteDetailsSteps({
    formData: form.formData,
    currentPreparedData: form.currentPreparedData,
    initialStep: step,
    quoteType: quote?.type
  });

  const { handleSave, handleCancel, mainLoading } = useQuoteDetailsSave({
    quote,
    formData: form.formData,
    currentPreparedData: form.currentPreparedData,
    steps: stepsState.steps,
    setShowWarning: stepsState.setShowWarning,
    setIsExpired: form.setIsExpired,
    mutations,
    onClose,
    updateQuoteInfo
  });

  const isLoading =
    mutations.isLoading || mutations.isUserUpdateLoading || mainLoading;

  return (
    <SBase
      disablePadding
      width="70vw"
      height="50vw"
      maxHeight={800}
      maxWidth={1200}
      header={{
        title: quote?.type === 'rfq' ? 'Edit Quote Details' : 'Quotes Settings',
        onClose,
        style: {
          background: 'white'
        }
      }}>
      <ContentContainer>
        {form.isExpired && quote?.type === 'quote' && (
          <ExpiredWarning
            quoteId={quote?._id}
            validUntil={validUntil}
            customDate={enableValidityDate}
            onSuccess={q => {
              updateQuoteInfo(q);
              form.setFormData(prev => ({
                ...prev,
                expirationDate: q?.validUntil
                  ? new Date(q.validUntil.toString().split('T')[0])
                  : moment().add(30, 'days').toDate()
              }));
              form.setIsExpired(false);
            }}
          />
        )}

        <Box display="flex" height="100%" overflow="hidden" position="relative">
          <StepperSidebar
            steps={stepsState.steps}
            activeStep={stepsState.activeStep}
            onStepClick={stepsState.handleStepClick}
          />

          <Content>
            <StepContentRenderer
              activeStep={stepsState.activeStep}
              formData={form.formData}
              currentPreparedData={form.currentPreparedData}
              isExpired={form.isExpired}
              enableValidityDate={enableValidityDate}
              updateFormData={form.updateFormData}
              setFormData={form.setFormData}
              setIsExpired={form.setIsExpired}
            />
          </Content>

          <ValidationWarning
            visible={stepsState.showWarning}
            onDismiss={() => stepsState.setShowWarning(false)}
          />
        </Box>

        <ModalFooter
          isLoading={isLoading}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      </ContentContainer>
    </SBase>
  );
};
